/**
 * Who is signed in, in which group, and in which experience (member or officer).
 *
 * - On start it asks the server (GET /me). A device that signed in before reopens to the
 *   PIN (status "locked"); a new device starts with phone and code.
 * - When the server refuses the session (15 minutes idle, PRD 12), the app locks and asks
 *   for the PIN again; nothing that moves money works while locked.
 * - The signed-in person's own figures are cached for offline viewing, and wiped at sign-out.
 * - Reopened with no signal within 15 minutes of her last tap (the app would not have locked yet),
 *   it shows those figures read-only; after that her PIN is needed, and the PIN is checked online.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { persistQueryClientRestore, persistQueryClientSubscribe } from '@tanstack/react-query-persist-client'
import { api, ApiError, CONSENT_REQUIRED_EVENT, SESSION_ENDED_EVENT } from '../api/client'
import { keys } from '../api/hooks'
import { clearPersonCaches, isPersistable, personPersister } from '../api/queryClient'
import type { QueryClient } from '@tanstack/react-query'
import type { Persister } from '@tanstack/react-query-persist-client'
import type { Me } from '../api/types'
import { readPreference, writePreference } from '../lib/storage'
import { idleFor, markActive, sharedLastActive, trackActivity } from './activity'
import { SessionContext, type Mode, type SessionStatus, type SessionValue, type UnlockInfo } from './useSession'

const UNLOCK_KEY = 'tisunge.unlock'
/** PRD 12: 15 minutes without use locks the app; the server ends the session at the same mark. */
const IDLE_LOCK_MS = 15 * 60 * 1000

function readUnlock(): UnlockInfo | null {
  try {
    const raw = window.localStorage.getItem(UNLOCK_KEY)
    return raw ? (JSON.parse(raw) as UnlockInfo) : null
  } catch {
    return null
  }
}

/** How her figures are kept on this phone: a week at most, and her own figures only. */
function persistOptions(queryClient: QueryClient, persister: Persister) {
  return {
    queryClient,
    persister,
    maxAge: 1000 * 60 * 60 * 24 * 7,
    buster: 'v2',
    dehydrateOptions: {
      shouldDehydrateQuery: (query: { queryKey: readonly unknown[]; state: { status: string } }) =>
        query.state.status === 'success' && isPersistable(query.queryKey),
    },
  }
}

function writeUnlock(info: UnlockInfo | null): void {
  try {
    if (info) window.localStorage.setItem(UNLOCK_KEY, JSON.stringify(info))
    else window.localStorage.removeItem(UNLOCK_KEY)
  } catch {
    // storage unavailable: she signs in with a code each time
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<SessionStatus>('loading')
  const [me, setMe] = useState<Me | null>(null)
  const [devTools, setDevTools] = useState(false)
  const [unlock, setUnlock] = useState<UnlockInfo | null>(readUnlock)
  const [groupId, setGroupId] = useState<string | null>(() => readPreference('group'))
  // Officers open in officer view until they choose otherwise; the choice is kept on this device.
  const [mode, setModeState] = useState<Mode | null>(() => readPreference('mode') as Mode | null)
  const [offline, setOffline] = useState(false)

  const lockOrSignOut = useCallback(() => {
    setStatus(readUnlock() ? 'locked' : 'signed-out')
  }, [])

  // Start: set the CSRF cookie, then ask who is signed in.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const csrf = await api.get<{ dev_tools: boolean }>('/auth/csrf')
        if (!cancelled) setDevTools(Boolean(csrf.dev_tools))
        const current = await api.get<Me>('/me')
        if (cancelled) return
        queryClient.setQueryData(keys.me, current)  // kept with her figures, for a reopen without signal
        setMe(current)
        setStatus('signed-in')
      } catch (error) {
        if (cancelled) return
        if (!(error instanceof ApiError && error.code === 'offline')) {
          lockOrSignOut()
          return
        }
        // No signal. Within the 15 minutes she would still be signed in, show what this phone kept, read-only.
        const info = readUnlock()
        const persister = info?.personId && Date.now() - sharedLastActive() < IDLE_LOCK_MS
          ? personPersister(info.personId) : null
        if (persister) {
          await persistQueryClientRestore(persistOptions(queryClient, persister))
          const kept = queryClient.getQueryData<Me>(keys.me)
          if (cancelled) return
          if (kept && kept.person.id === info?.personId) {
            setMe(kept)
            setOffline(true)
            setStatus('signed-in')
            return
          }
        }
        setStatus(info ? 'locked' : 'signed-out')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [lockOrSignOut, queryClient])

  useEffect(() => {
    const onEnded = () => lockOrSignOut()
    window.addEventListener(SESSION_ENDED_EVENT, onEnded)
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onEnded)
  }, [lockOrSignOut])

  // Consent withdrawn or never given: re-read who she is, so RequireSession sends her to the join step.
  useEffect(() => {
    const onConsent = () => {
      void api.get<Me>('/me').then(setMe).catch(() => undefined)
    }
    window.addEventListener(CONSENT_REQUIRED_EVENT, onConsent)
    return () => window.removeEventListener(CONSENT_REQUIRED_EVENT, onConsent)
  }, [])

  // Every tap or key counts as use, in any tab of this browser.
  useEffect(() => trackActivity(), [])

  // Lock after 15 minutes without a tap or a key in any tab, even if nothing asks the server meanwhile.
  useEffect(() => {
    if (status !== 'signed-in') return
    markActive()
    const check = () => {
      if (idleFor() < IDLE_LOCK_MS) return
      void api.post('/auth/logout').catch(() => undefined)
      lockOrSignOut()
    }
    document.addEventListener('visibilitychange', check)
    const timer = window.setInterval(check, 30_000)
    return () => {
      document.removeEventListener('visibilitychange', check)
      window.clearInterval(timer)
    }
  }, [status, lockOrSignOut])

  // Opened offline from what the phone kept: when the signal returns, the server says whether she is still in.
  useEffect(() => {
    if (!offline) return
    const back = () => {
      void api.get<Me>('/me').then((current) => {
        queryClient.setQueryData(keys.me, current)
        setMe(current)
        setOffline(false)
      }).catch(() => undefined)  // a refused session raises the session-ended event, which locks
    }
    window.addEventListener('online', back)
    return () => window.removeEventListener('online', back)
  }, [offline, queryClient])

  // Cache her own figures under her person id while she is signed in.
  const personId = me?.person.id ?? null
  useEffect(() => {
    if (!personId) return
    const persister = personPersister(personId)
    if (!persister) return
    const options = persistOptions(queryClient, persister)
    void persistQueryClientRestore(options)
    return persistQueryClientSubscribe(options)
  }, [personId, queryClient])

  const active = useMemo(() => (me?.memberships ?? []).filter((m) => m.status !== 'withdrawn'), [me])
  const membership = useMemo(() => active.find((m) => m.group.id === groupId) ?? active[0] ?? null,
    [active, groupId])

  const value = useMemo<SessionValue>(() => ({
    status,
    me,
    membership,
    groupId: membership?.group.id ?? null,
    mode: membership?.is_officer ? (mode ?? 'officer') : 'member',
    offline,
    devTools,
    unlock,
    signedIn: (next: Me, phone: string) => {
      setMe(next)
      setOffline(false)
      setStatus('signed-in')
      queryClient.setQueryData(keys.me, next)
      if (next.unlock_token) {
        const info = { phone, firstName: next.person.first_name, token: next.unlock_token, personId: next.person.id }
        writeUnlock(info)
        setUnlock(info)
      }
      const first = next.memberships[0]
      if (first && !next.memberships.some((m) => m.group.id === readPreference('group'))) {
        writePreference('group', first.group.id)
        setGroupId(first.group.id)
      }
    },
    setGroup: (id: string) => {
      writePreference('group', id)
      setGroupId(id)
    },
    setMode: (next: Mode) => {
      writePreference('mode', next)
      setModeState(next)
    },
    refresh: async () => {
      const current = await api.get<Me>('/me')
      queryClient.setQueryData(keys.me, current)
      setMe(current)
      setOffline(false)
    },
    signOut: async () => {
      await api.post('/auth/logout').catch(() => undefined)
      clearPersonCaches(queryClient)
      setMe(null)
      setStatus(readUnlock() ? 'locked' : 'signed-out')
    },
    forgetDevice: () => {
      writeUnlock(null)
      setUnlock(null)
      clearPersonCaches(queryClient)
      setStatus('signed-out')
    },
  }), [status, me, membership, mode, offline, devTools, unlock, queryClient])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}
